import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Input, Button, Space, Select } from 'antd';
import { EnvironmentOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Map, TileLayer, Marker, CircleMarker, Polygon, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { propertyService } from '@iso/services';

// Only used when there is no pin, no property areas and no property centre —
// roughly the middle of Australia, so the first pan is short wherever you are.
const DEFAULT_CENTER = [-25.2744, 133.7751];
const DEFAULT_ZOOM = 4;
const PINNED_ZOOM = 16;
// Roughly 165 km. Wider than any single property, narrow enough that one
// mis-located area cannot zoom the map out to a whole hemisphere.
const MAX_FIT_SPAN_DEGREES = 1.5;

// Matches the palette PropertyMap and the Hazard modal use for property areas.
const AREA_COLORS = {
  blue: '#31a6c5',
  green: '#52c41a',
  red: '#f5222d',
  yellow: '#faad14',
  purple: '#722ed1',
  orange: '#fa8c16',
};

// Coordinates are stored and displayed as ONE string, "lat, lng" — the shape a
// person reads off a phone GPS and can paste straight in. Six decimal places is
// ~0.1 m, well past what a handheld fix is worth, and trailing zeros are trimmed
// so a round number does not read as false precision.
const round6 = (n) => parseFloat(Number(n).toFixed(6));
export const formatLatLng = (lat, lng) => `${round6(lat)}, ${round6(lng)}`;

/**
 * Parse "lat, lng" (also tolerating "lat lng" and stray whitespace). Returns null
 * unless BOTH values are present and in range — a partially typed value must not
 * drop a marker somewhere misleading.
 */
export const parseLatLng = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  const match = value.match(/(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)/);
  if (!match) {
    return null;
  }
  const lat = parseFloat(match[1]);
  const lng = parseFloat(match[2]);
  if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }
  return [lat, lng];
};

const asNumber = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * A polygon's `area` may be a flat ring or nested rings. Collect every [lat, lng]
 * pair regardless of depth so bounds can be built from any shape.
 *
 * The API returns coordinates as STRINGS (`[["-37.440984", "144.93509"], …]`).
 * Leaflet coerces those happily when drawing, but `latLngBounds.extend` does not —
 * a numbers-only check silently yields empty bounds and the auto-zoom never fires.
 * A 2-element array of nested arrays is not a pair: those fail to parse and fall
 * through to the recursive branch.
 */
const collectPoints = (value, out = []) => {
  if (!Array.isArray(value)) {
    return out;
  }
  // The !isArray guards matter: parseFloat(['3','4']) is 3, because the array
  // coerces to the string "3,4". Without them a two-point RING would be misread
  // as a single coordinate pair built from the first number of each point.
  if (value.length === 2 && !Array.isArray(value[0]) && !Array.isArray(value[1])) {
    const lat = asNumber(value[0]);
    const lng = asNumber(value[1]);
    if (lat !== null && lng !== null) {
      out.push([lat, lng]);
      return out;
    }
  }
  value.forEach((item) => collectPoints(item, out));
  return out;
};

/**
 * A single-value location field: type coordinates in, or click the map to drop a
 * pin. Mirrors the Hazard and Incident modals — same Google hybrid tiles via a
 * plain Leaflet TileLayer (so no API key), and the property's areas drawn and
 * zoomed to, so the reporter sees their own paddocks rather than a blank world map.
 *
 * value / onChange speak the "lat, lng" string — NOT an object — so the answer
 * stores, exports and reads back as plain text like every other schema answer.
 *
 * showAreaSelect adds an area dropdown above the map: choosing one zooms to that
 * area so the pin can be dropped inside it. It only moves the view — the value
 * is still just the pin.
 *
 * referenceMarker ({ latitude, longitude, label }) draws a fixed point for
 * context — e.g. an existing map location — as a dot distinct from the pin, and
 * brings it into view whenever it changes. It is never part of the value.
 */
const LocationPicker = ({ value, onChange, disabled, placeholder, showAreaSelect, referenceMarker }) => {
  const mapRef = useRef(null);
  const didFitRef = useRef(false);
  // Guards against a stale property response landing after the user has switched
  // property, and against a pending timeout firing on a torn-down map.
  const propertyReqRef = useRef(0);
  const readyTimerRef = useRef(null);
  const [areas, setAreas] = useState([]);
  const [propertyCenter, setPropertyCenter] = useState(null);
  const [focusAreaId, setFocusAreaId] = useState(null);
  // The text box is free-typed, so it holds its own draft: re-deriving it from
  // `value` on every keystroke would fight the user mid-entry.
  const [text, setText] = useState(value || '');

  // The widget is rendered by antd-form-builder, which cannot pass extra props
  // through, so the property comes from the store. It is absent in the library
  // builder (no property at all) and on the PUBLIC visitor form (no session, so
  // the authed viewProperty call fails) — both degrade to a plain map.
  const activePropertyId = useSelector((state) => state.property.activePropertyId);

  useEffect(() => {
    setText(value || '');
  }, [value]);

  useEffect(() => {
    if (!activePropertyId) {
      setAreas([]);
      setPropertyCenter(null);
      return;
    }
    const req = ++propertyReqRef.current;
    propertyService.viewProperty(activePropertyId)
      .then((res) => {
        // Ignore a response that has been overtaken — switching property mid-flight
        // would otherwise draw the previous property's areas over the new one.
        if (req !== propertyReqRef.current) {
          return;
        }
        if (res && res.code === '0000') {
          // A new property means a new view: allow the map to re-fit.
          didFitRef.current = false;
          setAreas(Array.isArray(res.primary_objects) ? res.primary_objects : []);
          const p = res.property;
          const lat = p ? asNumber(p.lat) : null;
          const lng = p ? asNumber(p.lng) : null;
          setPropertyCenter(lat !== null && lng !== null ? [lat, lng] : null);
        }
      })
      .catch((err) => {
        // Expected on the public form, which has no session. Not an error state:
        // the field still works, it just cannot show the property's areas.
        console.error('LocationPicker could not load property areas:', err.message);
      });
  }, [activePropertyId]);

  // Cancel a pending post-ready timer if the field unmounts first — a drawer closed
  // inside the delay would otherwise run invalidateSize()/fitToAreas() on a
  // torn-down map.
  useEffect(() => () => {
    if (readyTimerRef.current) {
      clearTimeout(readyTimerRef.current);
    }
  }, []);

  const position = parseLatLng(text);
  const refLat = referenceMarker ? asNumber(referenceMarker.latitude) : null;
  const refLng = referenceMarker ? asNumber(referenceMarker.longitude) : null;
  const refPosition = refLat !== null && refLng !== null ? [refLat, refLng] : null;
  const hasRefPosition = !!refPosition;

  // Zoom to the property's areas, so the map opens on the reporter's own paddocks
  // instead of a continent. Skipped once a pin exists — the pin is what matters
  // then, and refitting would drag the view away from it.
  const fitToAreas = useCallback(() => {
    const map = mapRef.current;
    if (!map || position || hasRefPosition || !areas.length || didFitRef.current) {
      return;
    }
    try {
      const perArea = areas.map((area) => collectPoints(area.area)).filter((pts) => pts.length);
      if (!perArea.length) {
        return;
      }
      const boundsOf = (groups) => {
        const b = L.latLngBounds([]);
        groups.forEach((pts) => pts.forEach((pt) => b.extend(pt)));
        return b;
      };

      let bounds = boundsOf(perArea);
      // Guard against one stray area dragging the view out to a continental
      // span (real data has areas mis-keyed on the other side of the world).
      // Fall back to the FIRST area alone, which is the reference point the
      // Hazard modal uses too.
      if (bounds.isValid()) {
        const span = Math.max(
          Math.abs(bounds.getNorth() - bounds.getSouth()),
          Math.abs(bounds.getEast() - bounds.getWest())
        );
        if (span > MAX_FIT_SPAN_DEGREES) {
          bounds = boundsOf([perArea[0]]);
        }
      }

      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [24, 24], maxZoom: 17 });
        didFitRef.current = true;
      }
    } catch (e) {
      console.error('LocationPicker fitBounds failed:', e.message);
    }
  }, [areas, position, hasRefPosition]);

  // Bring a newly chosen reference point into view — alongside the pin when there
  // is one, so neither ends up off-screen. Keyed on the coordinates, not the
  // array, which is rebuilt every render. Before the map is ready, `center`
  // below already opens on it.
  const refKey = refPosition ? refPosition.join(',') : '';
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !refKey) {
      return;
    }
    const ref = refKey.split(',').map(Number);
    try {
      if (position) {
        map.fitBounds(L.latLngBounds([position, ref]), { padding: [40, 40], maxZoom: PINNED_ZOOM });
      } else {
        map.setView(ref, Math.max(map.getZoom(), PINNED_ZOOM));
      }
    } catch (e) {
      console.error('LocationPicker could not show the map location:', e.message);
    }
    // Only a change of reference point should move the map, not every pin drop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refKey]);

  // Areas arrive asynchronously, so fit whenever they land (or the map is ready).
  useEffect(() => {
    fitToAreas();
  }, [fitToAreas]);

  // Leaflet measures its container on mount. Inside a drawer/modal that is still
  // animating, that measurement is wrong and the tiles render as grey boxes until
  // something forces a re-measure.
  const handleReady = useCallback((event) => {
    const map = event && event.target ? event.target : null;
    mapRef.current = map;
    if (map) {
      if (readyTimerRef.current) {
        clearTimeout(readyTimerRef.current);
      }
      readyTimerRef.current = setTimeout(() => {
        readyTimerRef.current = null;
        try {
          map.invalidateSize();
        } catch (e) {
          console.error('LocationPicker invalidateSize failed:', e.message);
        }
        fitToAreas();
      }, 200);
    }
  }, [fitToAreas]);

  // Explicitly chosen, so unlike fitToAreas this zooms even when a pin exists.
  const focusArea = (areaId) => {
    setFocusAreaId(areaId === undefined ? null : areaId);
    const map = mapRef.current;
    const area = areas.find((a) => a.id === areaId);
    const points = area ? collectPoints(area.area) : [];
    if (!map || !points.length) {
      return;
    }
    try {
      map.fitBounds(L.latLngBounds(points), { padding: [24, 24], maxZoom: 18 });
      didFitRef.current = true;
    } catch (e) {
      console.error('LocationPicker could not zoom to area:', e.message);
    }
  };

  const areaOptions = areas
    .filter((area) => collectPoints(area.area).length)
    .map((area) => ({ value: area.id, label: area.name || 'Unnamed Area' }));

  const commit = (lat, lng) => {
    const next = formatLatLng(lat, lng);
    setText(next);
    if (onChange) {
      onChange(next);
    }
  };

  const handleMapClick = (event) => {
    if (disabled || !event || !event.latlng) {
      return;
    }
    commit(event.latlng.lat, event.latlng.lng);
  };

  const handleMarkerDrag = (event) => {
    if (disabled || !event || !event.target) {
      return;
    }
    const latlng = event.target.getLatLng();
    commit(latlng.lat, latlng.lng);
  };

  // Typing is passed straight through — a half-typed value is still the user's
  // value. The map simply shows no marker until it parses.
  const handleTextChange = (e) => {
    const next = e.target.value;
    setText(next);
    if (onChange) {
      onChange(next);
    }
  };

  const clear = () => {
    setText('');
    // Allow the view to snap back to the property once the pin is gone.
    didFitRef.current = false;
    if (onChange) {
      onChange('');
    }
  };

  // Priority: the pin, else the property's areas (handled by fitBounds), else the
  // property centre, else a continent-wide view.
  const center = position || refPosition || propertyCenter || DEFAULT_CENTER;
  const zoom = (position || refPosition) ? PINNED_ZOOM : (propertyCenter ? 14 : DEFAULT_ZOOM);

  return (
    <div>
      {showAreaSelect && !disabled && areaOptions.length > 0 && (
        <Select
          showSearch
          allowClear
          value={focusAreaId}
          onChange={focusArea}
          options={areaOptions}
          optionFilterProp="label"
          placeholder="Find an area on the map"
          aria-label="Find an area on the map"
          style={{ width: '100%', marginBottom: 8 }}
        />
      )}
      <Space.Compact style={{ width: '100%' }}>
        <Input
          value={text}
          onChange={handleTextChange}
          disabled={disabled}
          allowClear={false}
          prefix={<EnvironmentOutlined />}
          placeholder={placeholder || 'e.g. -27.469800, 153.025100 — or click the map below'}
        />
        {!disabled && text ? (
          <Button icon={<CloseCircleOutlined />} onClick={clear} title="Clear location" />
        ) : null}
      </Space.Compact>

      <div style={{ marginTop: 8, borderRadius: 4, overflow: 'hidden' }}>
        <Map
          center={center}
          zoom={zoom}
          doubleClickZoom={false}
          zoomControl
          onClick={handleMapClick}
          whenReady={handleReady}
          style={{ width: '100%', height: 260 }}
        >
          <TileLayer
            attribution="&copy; Google Maps"
            url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
          />
          {areas.map((area) => {
            const points = area.area;
            if (!points || !collectPoints(points).length) {
              return null;
            }
            const color = AREA_COLORS[area.color] || AREA_COLORS.blue;
            const focused = area.id === focusAreaId;
            return (
              <Polygon
                key={area.id}
                positions={points}
                fillColor={color}
                color={color}
                fillOpacity={focused ? 0.35 : 0.2}
                weight={focused ? 4 : 2}
              >
                <Tooltip permanent direction="center" className="primary-objects-tooltip">
                  <div>
                    <p style={{ margin: 0, fontWeight: 'bold' }}>{area.name || 'Unnamed Area'}</p>
                  </div>
                </Tooltip>
              </Polygon>
            );
          })}
          {refPosition && (
            // Clicks pass through to the map, so a pin can be dropped right on it.
            <CircleMarker
              center={refPosition}
              radius={9}
              color="#ffffff"
              weight={2}
              fillColor={AREA_COLORS.orange}
              fillOpacity={1}
            >
              <Tooltip permanent direction="top" offset={[0, -10]}>
                {referenceMarker.label || 'Map location'}
              </Tooltip>
            </CircleMarker>
          )}
          {position && (
            <Marker
              position={position}
              draggable={!disabled}
              onDragend={handleMarkerDrag}
            />
          )}
        </Map>
      </div>

      <div style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.45)', marginTop: 4 }}>
        {disabled
          ? 'Location'
          : 'Click the map to drop a pin, drag the pin to adjust, or type coordinates above.'}
        {refPosition ? ' The orange dot is the selected map location.' : ''}
      </div>
    </div>
  );
};

export default LocationPicker;
